import { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Clock, ArrowRight } from "lucide-react";
import { Reveal, SectionHead } from "@/components/Reveal";
import { EVENTS } from "@/content";

export default function Events() {
  const [showAll, setShowAll] = useState(false);
  const visible = showAll ? EVENTS : EVENTS.slice(0, 3);

  return (
    <section id="events" data-testid="events-section" className="relative py-20 lg:py-32">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_10%_80%,rgba(255,0,85,0.07),transparent_50%)]" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHead
          eyebrow="On Tour"
          title={<>Upcoming <span className="text-neon">Events</span></>}
          sub="Catch QT 2hype live. New dates drop every month — lock yours in before the night sells out."
        />

        <div className="flex gap-6 overflow-x-auto snap-x snap-mandatory pb-4 -mx-4 px-4 md:mx-0 md:px-0 md:grid md:grid-cols-3 md:overflow-visible">
          {visible.map((e, i) => (
            <motion.article
              key={e.id}
              data-testid={`event-card-${e.id}`}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.7, delay: (i % 3) * 0.12, ease: [0.22, 1, 0.36, 1] }}
              className="group min-w-[82%] sm:min-w-[340px] md:min-w-0 snap-center glass rounded-2xl overflow-hidden hover:border-volt/40 hover:shadow-[0_0_35px_rgba(204,255,0,0.12)] transition-all"
            >
              <div className="relative h-52 overflow-hidden">
                <img
                  src={e.artwork}
                  alt={`${e.name} event artwork`}
                  loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-ink/80 to-transparent" />
                <div className="absolute top-4 left-4 bg-volt text-ink px-4 py-2 font-head font-extrabold leading-none [clip-path:polygon(0_0,100%_0,88%_100%,0_100%)]">
                  {e.day && <span className="block text-2xl">{e.day}</span>}
                  <span className={`block tracking-[0.2em] ${e.day ? "text-[11px]" : "text-sm"}`}>{e.month}</span>
                </div>
              </div>
              <div className="p-6">
                <h3 className="font-head font-extrabold uppercase tracking-tight text-xl">{e.name}</h3>
                <p className="mt-3 flex items-center gap-2 text-sm text-zinc-400">
                  <MapPin className="w-4 h-4 text-mag" /> {e.venue} - {e.city}
                </p>
                <p className="mt-2 flex items-center gap-2 text-sm text-zinc-400">
                  <Clock className="w-4 h-4 text-cy" /> {e.time}
                </p>
                <a
                  href={e.ticketUrl || "#booking"}
                  {...(e.ticketUrl ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                  data-testid={`event-ticket-${e.id}`}
                  className="mt-5 inline-flex items-center gap-2 font-head font-bold uppercase text-sm tracking-wider text-volt hover:gap-3.5 transition-all"
                >
                  Get Tickets / Info <ArrowRight className="w-4 h-4" />
                </a>
              </div>
            </motion.article>
          ))}
        </div>

        <Reveal className="mt-12 text-center">
          <button
            data-testid="events-see-all-button"
            onClick={() => setShowAll(!showAll)}
            className="glass font-head font-bold uppercase tracking-wider px-10 py-4 rounded-full hover:border-volt/60 hover:text-volt transition-colors"
          >
            {showAll ? "Show Less" : "See All Events"}
          </button>
        </Reveal>
      </div>
    </section>
  );
}
